import { FC, useMemo, useState } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { useParams } from "react-router-dom";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { Domain } from "@hostingbv/lib/src/api/partner/models/Domain";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DNSZone } from "@hostingbv/lib/src/api/partner/models/DNSZone";
import { DnsEditor } from "@hostingbv/lib/src/components/dns-editor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { usePermissions } from "../../../lib/src/auth/permissions";
import { faGlobe, faPersonDigging } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { Deployment } from "../../../lib/src/api/partner/models/Deployment";

export const DomainDnsEditor: FC = () => {
  const { uuid } = useParams()
  const { partnerClient } = useApiClient(config)
  const { resource: domainResource, reload: reloadDomain } = useFetchedResource(() => partnerClient.getDomain(uuid ?? ""))
  const domain: Domain | null = useMemo(() => domainResource?.success === true ? domainResource.data : null, [domainResource])

  const { resource: dnsResource, reload: reloadDns, loading: dnsLoading } = useFetchedResource(() => partnerClient.getDNSZone(uuid ?? ""))
  const dns: DNSZone | null = useMemo(() => dnsResource?.success === true ? dnsResource.data : null, [dnsResource])

  const [waitingForDeployment, setWaitingForDeployment] = useState(false)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const { can } = usePermissions(partnerClient)

  useRefreshEffect(() => {
    reloadDomain().then((response) => {
      if (response && response.success && (response.data.deploymentStatus === "pending" || response.data.deploymentStatus === "running")) {
        setWaitingForDeployment(true)
        awaitNextDeploymentFinished(domain!.id).then(() => {
          setWaitingForDeployment(false)
          reloadDns()
        })
      }
    })
    reloadDns()
  })
  const onSave = (deployment: Deployment) => {
    setWaitingForDeployment(true)
    awaitNextDeploymentFinished(deployment.id).then(() => {
      reloadDns()
      setWaitingForDeployment(false)
    })
  }


  return (
    <ContentContainer size={"lg"}>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs currentPage={'DNS Editor'} crumbs={[
          { label: "Domains", href: "/domains" },
          { label: domain?.displayName ?? "Domain", href: domain ? `/domains/${domain.id}` : '/domains' },
        ]} />
        <PageHeader>DNS Editor</PageHeader>
      </PageIconHeader>

      <div className={"mb-96"}>
        {waitingForDeployment ? <div className={"bg-white border-2 border-slate-100 rounded-lg p-8 flex flex-col items-center justify-center space-y-6"}>
          <FontAwesomeIcon icon={faPersonDigging} className={"text-2xl"} />
          <h2 className={"text-xl font-bold text-slate-700"}>Deploying changes...</h2>
          <p className={"text-slate-500"}>This may take a few seconds. Please wait.</p>
        </div> : <DnsEditor readonlyMode={!can('edit', 'dns', domain?.permissions)} domain={domain} saveCall={partnerClient.updateDNSZone.bind(partnerClient)} onSave={onSave} records={dnsLoading ? undefined : dns?.records} />}
      </div>
    </ContentContainer>
  );
}

